import { getAssetPendingNonExchangeTxs, isEthereumLikeAsset } from '@exodus/ethereum-lib';
import { useSelectors } from '@exodus/headless-react';
import ms from 'ms';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { MAX_ALLOWED_SPREAD_PERCENTAGE, NEGATIVE_SPREAD_THRESHOLD } from '../constants.js';
import useExchangeBalance from './use-exchange-balance.js';
import useExchangeSpreadPercent from './use-exchange-spread-percent.js';
import useFromAmount from './use-from-amount.js';
import useFromAsset from './use-from-asset.js';
import useFromWalletAccount from './use-from-wallet-account.js';
import useToAmount from './use-to-amount.js';
import useToAsset from './use-to-asset.js';
import useToWalletAccount from './use-to-wallet-account.js';
import useExchangeTotalBalance from './use-exchange-total-balance.js';
import useAssetReady from './use-asset-ready.js';
import useExchangeAvailability from './use-exchange-availability.js';
import useSwapMode from './use-swap-mode.js';
const EMPTY_OBJECT = Object.freeze({
    currentError: null,
    preventFromExchanging: false,
});
const useShowEosAccountCreationInProgress = () => {
    const selectors = useSelectors();
    const [toAsset] = useToAsset();
    const [toWalletAccount] = useToWalletAccount();
    const incomingOrders = useSelector(selectors.orders.assetIncomingOrders)({
        assetName: toAsset.name,
        walletAccount: toWalletAccount.toString(),
    });
    const accountState = useSelector(selectors.accountStates.createAssetSourceSelector({
        walletAccount: toWalletAccount.toString(),
        assetName: toAsset.name,
    }));
    const eosioAccountExists = Boolean(accountState?.mainAccountName);
    return useMemo(() => toAsset.name === 'eosio' && !eosioAccountExists && incomingOrders.size > 0, [eosioAccountExists, incomingOrders.size, toAsset]);
};
const useExchangeErrors = (params) => {
    const { fee, quoteError, quoteLoading, limits, limitsLoading, isSendAll } = params;
    const selectors = useSelectors();
    const [fromAmount] = useFromAmount();
    const [fromAsset] = useFromAsset();
    const [toAsset] = useToAsset();
    const [toAmount] = useToAmount();
    const [fromWalletAccount] = useFromWalletAccount();
    const [toWalletAccount] = useToWalletAccount();
    const exchangeAvailability = useExchangeAvailability();
    const [swapMode] = useSwapMode();
    const txLog = useSelector(selectors.txLog.createAssetSourceSelector({
        assetName: fromAsset.baseAsset.name,
        walletAccount: fromWalletAccount.toString(),
    }));
    const getIsExchangeTx = useSelector(selectors.orders.getIsExchangeTx);
    const showEosAccountCreationInProgress = useShowEosAccountCreationInProgress();
    const fromAssetBalance = useExchangeBalance({
        assetName: fromAsset.name,
        walletAccount: fromWalletAccount.toString(),
    });
    const feeAssetBalance = useExchangeBalance({
        assetName: fromAsset.feeAsset.name,
        walletAccount: fromWalletAccount.toString(),
    });
    const totalBalance = useExchangeTotalBalance();
    const fromAmountToCompare = isSendAll ? totalBalance : fromAmount;
    const spreadPercentage = useExchangeSpreadPercent({
        fromAsset,
        toAsset,
        fromAmount: fromAmountToCompare,
        toAmount,
    });
    const getExchangeWarningOverride = useSelector(selectors.exchange.remoteWarning);
    const { exchangeWarningText, exchangeWarningTitle } = getExchangeWarningOverride(fromAsset, toAsset);
    const isMoneroOutOfDate = useSelector(selectors.assets.moneroIsOutOfDate);
    const getAlgoMustOptIn = useSelector(selectors.assets.algoMustOptIn);
    const { assetReady: fromAssetReady, loading: loadingFromAssetReady } = useAssetReady({
        assetName: fromAsset.name,
        walletAccount: fromWalletAccount.toString(),
    });
    const { assetReady: toAssetReady, loading: loadingToAssetReady } = useAssetReady({
        assetName: toAsset.name,
        walletAccount: toWalletAccount.toString(),
    });
    const errors = [];
    if (!fromAssetReady && !loadingFromAssetReady) {
        errors.push({
            type: 'warning',
            reason: 'FROM_ASSET_NOT_READY',
            preventFromExchanging: true,
            payload: { fromAsset, toAsset, fromWalletAccount, toAmount },
        });
    }
    if (!toAssetReady && !loadingToAssetReady) {
        errors.push({
            type: 'warning',
            reason: 'TO_ASSET_NOT_READY',
            preventFromExchanging: true,
            payload: { fromAsset, toAsset, toWalletAccount, toAmount },
        });
    }
    if (!fromAsset.units.hasOwnProperty(fromAmount.unitName)) {
        return EMPTY_OBJECT;
    }
    if (exchangeAvailability && !exchangeAvailability.isAvailable) {
        errors.push({
            type: 'error',
            reason: 'EXCHANGE_UNAVAILABLE',
            payload: exchangeAvailability.reason,
            preventFromExchanging: true,
        });
    }
    if (toAsset.assetType === 'ALGORAND_TOKEN' &&
        getAlgoMustOptIn(toAsset.name, toWalletAccount.toString())) {
        errors.push({
            type: 'error',
            reason: 'MUST_OPT_IN',
            payload: { toAsset, toWalletAccount: toWalletAccount.toString() },
            preventFromExchanging: true,
        });
    }
    if (fromAsset.assetType === 'ALGORAND_TOKEN' &&
        fromAmount.isPositive &&
        fee?.gt(feeAssetBalance)) {
        const fromAccountReserve = fromAsset.feeAsset.accountReserve || fromAsset.feeAsset.currency.ZERO;
        errors.push({
            type: 'error',
            reason: 'LOW_ALGORAND_BALANCE',
            payload: { fromAsset, fromAccountReserve, fee, walletAccount: fromWalletAccount.toString() },
            preventFromExchanging: false,
        });
    }
    const isFromFeeAsset = fromAsset.name === fromAsset.feeAsset.name;
    if (fromAmount.gt(fromAssetBalance) ||
        (fee && isFromFeeAsset && fromAssetBalance.sub(fee).lt(fromAsset.currency.ZERO)) ||
        fromAmountToCompare.gt(fromAssetBalance) ||
        fromAmountToCompare.gt(totalBalance)) {
        errors.push({
            type: 'error',
            reason: 'NOT_ENOUGH_BALANCE',
            payload: {
                fromAssetBalance,
                exchangeableBalance: totalBalance,
                fromAsset,
                walletAccount: fromWalletAccount.toString(),
            },
            preventFromExchanging: true,
        });
    }
    const amountToCompare = swapMode === 'ExactIn' ? fromAmountToCompare : toAmount;
    const limitsToCompare = swapMode === 'ExactIn' ? limits?.ExactIn : limits?.ExactOut;
    if (limitsToCompare && amountToCompare.isPositive) {
        if (amountToCompare.lt(limitsToCompare.min)) {
            errors.push({
                type: 'error',
                reason: 'LOW_FROM_AMOUNT',
                preventFromExchanging: true,
                payload: {
                    limits: limitsToCompare,
                    asset: swapMode === 'ExactIn' ? fromAsset : toAsset,
                    walletAccount: swapMode === 'ExactIn' ? fromWalletAccount.toString() : toWalletAccount.toString(),
                },
            });
        }
        if (amountToCompare.gt(limitsToCompare.max)) {
            errors.push({
                type: 'error',
                reason: 'HIGH_FROM_AMOUNT',
                preventFromExchanging: true,
                payload: { limits: limitsToCompare, asset: swapMode === 'ExactIn' ? fromAsset : toAsset },
            });
        }
    }
    if (fee &&
        fromAmountToCompare.isPositive &&
        fromAsset.name !== fromAsset.feeAsset.name &&
        feeAssetBalance.lt(fee)) {
        errors.push({
            type: 'error',
            reason: 'NOT_ENOUGH_FEE_BALANCE',
            preventFromExchanging: true,
            payload: { fee, fromAsset, walletAccount: fromWalletAccount.toString() },
        });
    }
    if ((quoteError && !quoteLoading) || (!limitsLoading && !limits)) {
        errors.push({
            type: 'error',
            reason: 'PAIR_UNAVAILABLE',
            preventFromExchanging: true,
            payload: undefined,
        });
    }
    if (fromAsset.name === 'monero' && isMoneroOutOfDate) {
        errors.push({
            type: 'error',
            reason: 'MONERO_OUT_OF_DATE',
            preventFromExchanging: true,
            payload: { fromAsset },
        });
    }
    if (showEosAccountCreationInProgress) {
        errors.push({
            type: 'error',
            reason: 'EOS_ACCOUNT_CREATION_IN_PROGRESS',
            preventFromExchanging: true,
            payload: undefined,
        });
    }
    if (isEthereumLikeAsset(fromAsset.baseAsset)) {
        const pendingTxs = getAssetPendingNonExchangeTxs(fromAsset.baseAsset.name, fromWalletAccount.toString(), () => txLog, getIsExchangeTx);
        const now = Date.now();
        if (pendingTxs.some((tx) => now - tx.date.getTime() > ms('5m'))) {
            errors.push({
                type: 'error',
                reason: 'TRANSACTION_STUCK',
                preventFromExchanging: true,
                payload: { fromAsset },
            });
        }
    }
    if (spreadPercentage && spreadPercentage > MAX_ALLOWED_SPREAD_PERCENTAGE && !quoteLoading) {
        errors.push({
            type: 'error',
            reason: 'HIGH_PRICE_IMPACT',
            preventFromExchanging: false,
            payload: { spreadPercentage },
        });
    }
    if (spreadPercentage && spreadPercentage < NEGATIVE_SPREAD_THRESHOLD && !quoteLoading) {
        errors.push({
            type: 'error',
            reason: 'NEGATIVE_SPREAD',
            preventFromExchanging: false,
            payload: { spreadPercentage },
        });
    }
    if (exchangeWarningText && exchangeWarningTitle) {
        errors.push({
            type: 'warning',
            reason: 'WARNING',
            preventFromExchanging: false,
            payload: { fromAsset, toAsset, text: exchangeWarningText, title: exchangeWarningTitle },
        });
    }
    const preventFromExchanging = errors.some((error) => error.preventFromExchanging);
    const currentError = errors[0];
    return { currentError, preventFromExchanging };
};
export default useExchangeErrors;
